import { User } from "../components/types/User";
import { whenIdle } from "./whenIdle";

/**
 * Microsoft Clarity integration.
 *
 * Loaded from JS (not public/index.html) so initialization stays
 * symmetric with PostHog and GA4 — single source of truth, single
 * env-var gate, identify() hook tied to the same UserContext.
 *
 * REACT_APP_CLARITY_PROJECT_ID is the short public project ID shown
 * in Clarity → Settings → Setup (e.g. "wwfd9o2xn3"). It is NOT the
 * Data Export JWT in services/.env CLARITY_API_TOKEN — that one is
 * for the backend growth scanner. Both belong to the same project.
 */

declare global {
  interface Window {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const projectId = process.env.REACT_APP_CLARITY_PROJECT_ID;

let initialized = false;

export const initClarity = () => {
  if (initialized || !projectId || typeof window === "undefined") {
    return;
  }

  // Canonical Clarity install snippet, ported to TS. Keeps a queue
  // so calls to window.clarity(...) made before the external script
  // finishes loading are replayed once it does. The stub goes in now
  // and only the script waits for idle.
  const c = window as Window & {
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[][] };
  };
  c.clarity =
    c.clarity ||
    Object.assign(
      function (...args: unknown[]) {
        (c.clarity!.q = c.clarity!.q || []).push(args);
      },
      { q: [] as unknown[][] }
    );

  initialized = true;

  whenIdle(() => {
    const tag = document.createElement("script");
    tag.async = true;
    tag.src = `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`;
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript?.parentNode?.insertBefore(tag, firstScript);
  });
};

export const identifyClarityUser = (user: User) => {
  if (!initialized || !user?.id || typeof window === "undefined" || !window.clarity) {
    return;
  }
  // clarity('identify', customId, customSessionId?, customPageId?, friendlyName?)
  // See https://learn.microsoft.com/clarity/setup-and-installation/identify-api
  window.clarity("identify", String(user.id), undefined, undefined, user.email || undefined);
  if (user.role) {
    window.clarity("set", "role", String(user.role));
  }
};

export const resetClarity = () => {
  if (!initialized || typeof window === "undefined" || !window.clarity) {
    return;
  }
  // Clarity has no built-in logout/reset. The closest behavior is to
  // re-identify with a fresh anonymous id so the next session is not
  // stitched to the previous user. We let Clarity assign its own id
  // by not calling identify again — but we DO scrub user_properties.
  window.clarity("set", "role", "");
};
